import { NotAuthenticatedError, isResumeExcludedPath, type FlowResponse, type ResumeAttemptOutcome, type UnauthenticatedInfo, type WhoAmIResponse } from '@carculate/api-client';

import { clearFitterOfflineCache } from '@/lib/fitter-offline/wipe';

import { isFitterPath, isLoginPath, isPasswordResetPath } from './loginPath';

/**
 * Story `fe-be-monteur-s5c-ingelogd-blijven` (EPIC-021, S021.5c) §3.2 — de HERVAT-COÖRDINATOR.
 *
 * Krijgt de app op `/monteur…` een 401 (whoami bij het openen, een gewone call, of de
 * foto-wachtrij), dan probeert hij ÉÉN keer de sessie te hervatten met het toestel-token
 * (`POST /api/auth/flow/resume`) vóórdat er naar het inlogscherm genavigeerd wordt. De
 * `ApiClient` roept {@link ResumeCoordinator.attempt} aan bij elke 401 en herhaalt het verzoek
 * bij `resumed` één keer (B-T4).
 *
 * Wanneer NIET (review A-12, DT-5): buiten `/monteur…` (kantoor: nooit), op een inlogscherm
 * (een fout wachtwoord zou anders stil inloggen als de eigenaar van het token), na uitloggen
 * (de lokale markering, review A-11), en bij 401's van de inlog-routes zelf.
 *
 * Eén poging per golf (AC 18): gelijktijdige 401's delen dezelfde promise. Tussen tabbladen een
 * Web Lock (`carculate-resume`); wie op de lock moest wachten, kijkt eerst of whoami al werkt
 * (een ander tabblad heeft dan hervat, de sessiecookie is gedeeld) — zonder Web Locks (oudere
 * iOS) blijft de gedeelde promise binnen het tabblad over (F-14).
 *
 * 503/429/netwerk → `unavailable`: GEEN inlogscherm, en een oplopende wachttijd (5 s → 30 s →
 * 2 min) zodat een storing geen stormloop wordt (TTA F-5).
 */

/** De naam van de Web Lock (story §3.2). */
export const RESUME_LOCK_NAME = 'carculate-resume';

/** Window-event na een geslaagde hervatting; `detail` = de nieuwe identiteit (of `null`). */
export const SESSION_RESUMED_EVENT = 'carculate:session-resumed';

/** localStorage-sleutel: deze monteur heeft uitgelogd → niet meer hervatten tot een interactieve inlog. */
export const LOGGED_OUT_KEY = 'carculate.fitter.loggedOut';

/** localStorage-sleutel: het afmelden van dit toestel moet nog naar de server (offline uitgelogd). */
export const FORGET_PENDING_KEY = 'carculate.fitter.forgetPending';

/** Oplopende wachttijden na `unavailable` (ms). */
export const RESUME_BACKOFF_MS = [5_000, 30_000, 120_000] as const;

/** De wachttijd na de `n`-de opeenvolgende mislukking (0-gebaseerd); daarna blijft hij op de laatste. */
export function backoffFor(n: number): number {
    return RESUME_BACKOFF_MS[Math.min(Math.max(n, 0), RESUME_BACKOFF_MS.length - 1)] ?? 120_000;
}

// ─── Lokale markeringen (try/catch: privé-venster, geblokkeerde opslag) ──────

function readFlag(key: string): boolean {
    try {
        return typeof localStorage !== 'undefined' && localStorage.getItem(key) === '1';
    } catch {
        return false;
    }
}

function writeFlag(key: string, on: boolean): void {
    try {
        if (typeof localStorage === 'undefined') return;
        if (on) localStorage.setItem(key, '1');
        else localStorage.removeItem(key);
    } catch {
        /* opslag onbereikbaar: dan alleen het servergedrag */
    }
}

/** Is er op dit toestel uitgelogd (en sindsdien niet interactief ingelogd)? */
export function isLoggedOut(): boolean {
    return readFlag(LOGGED_OUT_KEY);
}

/** Bij uitloggen: hervatten blokkeren tot de volgende interactieve inlog (review A-11). */
export function markLoggedOut(): void {
    writeFlag(LOGGED_OUT_KEY, true);
}

/**
 * Na een geslaagde interactieve inlog: hervatten mag weer. Een nog openstaand afmelden vervalt
 * ook: de inlog heeft een oude reeks op dit toestel server-side al dichtgezet (review A-10), en
 * een late `forget-device` zou anders het NIEUWE token van deze inlog intrekken.
 */
export function clearLoggedOut(): void {
    writeFlag(LOGGED_OUT_KEY, false);
    writeFlag(FORGET_PENDING_KEY, false);
}

/**
 * Bij uitloggen ALTIJD het toestel afmelden. Lukt dat niet (geen netwerk, 503), dan blijft er
 * een markering staan en probeert {@link flushPendingForgetDevice} het later opnieuw.
 */
export async function forgetThisDevice(forget: () => Promise<unknown>): Promise<boolean> {
    try {
        await forget();
        writeFlag(FORGET_PENDING_KEY, false);
        // S021.5d §A.6 W4: het toestel is afgemeld → de offline-cache leeg (ook via de pending-route).
        // Gate O-1: NIET afwachten — uitloggen (en het inloggen dat een pending forget doorzet) mag
        // nooit op een hangende IndexedDB blijven wachten; het wissen loopt op de achtergrond.
        void clearFitterOfflineCache('forget-device');
        return true;
    } catch {
        writeFlag(FORGET_PENDING_KEY, true);
        return false;
    }
}

/** Een eerder mislukt afmelden opnieuw proberen (bij `online` en bij het openen van de app). */
export async function flushPendingForgetDevice(forget: () => Promise<unknown>): Promise<void> {
    if (!readFlag(FORGET_PENDING_KEY)) return;
    await forgetThisDevice(forget);
}

// ─── De beslissing ────────────────────────────────────────────────────────────

/** DT-5 — start een 401 op dit pad, van deze route, een hervatting? */
export function shouldAttemptResume(pathname: string | null | undefined, requestPath: string, loggedOut: boolean): boolean {
    if (!isFitterPath(pathname)) return false; // kantoor (en /monteurs): nooit
    if (isLoginPath(pathname)) return false; // op het inlogscherm: nooit (A-12)
    // be-fe-wachtwoord-vergeten: de wachtwoord-vergeten/-herstellen-schermen zijn publiek; daar nooit stil inloggen.
    if (isPasswordResetPath(pathname)) return false;
    if (loggedOut) return false; // na uitloggen: nooit (A-11)
    if (isResumeExcludedPath(requestPath)) return false; // inlog-routes, resume zelf, logout
    return true;
}

// ─── De coördinator ───────────────────────────────────────────────────────────

export interface ResumeCoordinatorDeps {
    /** `POST /api/auth/flow/resume` (zonder auth-hooks). */
    resume: () => Promise<FlowResponse>;
    /** `GET /api/auth/whoami` zonder auth-hooks. */
    whoAmIQuiet: () => Promise<WhoAmIResponse>;
    /** Het huidige pad (op het moment van de 401). */
    pathname: () => string;
    /** Web Locks, of `null` zonder (oudere iOS, http op het LAN). */
    locks: LockManager | null;
    now: () => number;
    /** Na een geslaagde hervatting (identiteit uit het antwoord of uit whoami). */
    onResumed: (identity: WhoAmIResponse | null) => void;
    loggedOut?: () => boolean;
}

export interface ResumeCoordinator {
    attempt(info: UnauthenticatedInfo): Promise<ResumeAttemptOutcome>;
    /** Vergeet de lopende poging en de wachttijd (tests; en na een interactieve inlog). */
    reset(): void;
}

export function createResumeCoordinator(deps: ResumeCoordinatorDeps): ResumeCoordinator {
    let inFlight: Promise<ResumeAttemptOutcome> | null = null;
    let failures = 0;
    let nextAllowedAt = 0;

    const classify = (err: unknown): ResumeAttemptOutcome =>
        err instanceof NotAuthenticatedError ? 'login-required' : 'unavailable';

    async function doResume(): Promise<ResumeAttemptOutcome> {
        try {
            const res = await deps.resume();
            deps.onResumed(res?.identity ?? null);
            return 'resumed';
        } catch (err) {
            return classify(err);
        }
    }

    async function checkWhoAmIThenResume(): Promise<ResumeAttemptOutcome> {
        try {
            const me = await deps.whoAmIQuiet();
            deps.onResumed(me ?? null);
            return 'resumed';
        } catch (err) {
            if (!(err instanceof NotAuthenticatedError)) return 'unavailable';
        }
        return doResume();
    }

    async function run(): Promise<ResumeAttemptOutcome> {
        const locks = deps.locks;
        if (!locks) return doResume();

        // Eerst zonder wachten: wie de lock meteen krijgt, hervat.
        const first = await locks.request(RESUME_LOCK_NAME, { ifAvailable: true }, async (lock) =>
            lock ? { got: true as const, outcome: await doResume() } : { got: false as const },
        );
        if (first.got) return first.outcome;
        // Een ander tabblad hervat: wachten, en daarna eerst kijken of whoami al werkt.
        return locks.request(RESUME_LOCK_NAME, () => checkWhoAmIThenResume());
    }

    return {
        attempt(info) {
            const loggedOut = deps.loggedOut ? deps.loggedOut() : isLoggedOut();
            if (!shouldAttemptResume(deps.pathname(), info.path, loggedOut)) return Promise.resolve('skipped');
            if (inFlight) return inFlight;
            if (deps.now() < nextAllowedAt) return Promise.resolve('unavailable');

            inFlight = run()
                .catch((): ResumeAttemptOutcome => 'unavailable')
                .then((outcome) => {
                    if (outcome === 'unavailable') {
                        nextAllowedAt = deps.now() + backoffFor(failures);
                        failures++;
                    } else {
                        failures = 0;
                        nextAllowedAt = 0;
                    }
                    return outcome;
                })
                .finally(() => {
                    inFlight = null;
                });
            return inFlight;
        },
        reset() {
            inFlight = null;
            failures = 0;
            nextAllowedAt = 0;
        },
    };
}
